import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import { alpha } from "@mui/material/styles";
import i18next from "i18next";
import * as React from "react";
import { constants } from "../../utils";

interface EnhancedTableProps {
	numSelected: number;
	onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	rowCount: number;
	columns: Array<any>;
}

function EnhancedTableHead(props: EnhancedTableProps) {
	return (
		<TableHead>
			<TableRow>
				{props.columns.map((headCell) => (
					<TableCell key={headCell.id} align={"left"} padding={headCell.disablePadding ? "none" : "normal"}>
						<div className="ps-3 fw-bold">{headCell.label}</div>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

interface EnhancedTableToolbarProps {
	numSelected: number;
	filtersComponents: React.ReactNode;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
	const { numSelected, filtersComponents } = props;

	return (
		<Toolbar
			sx={{
				pl: { sm: 2 },
				pr: { xs: 1, sm: 1 },
				...(numSelected > 0 && {
					bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
				}),
			}}
		>
			{filtersComponents}
		</Toolbar>
	);
}

interface Props {
	columns: Array<{}>;
	rows: Array<{}>;
	total: number;
	filtersComponents?: React.ReactNode;
	onRowPress: ((row: any) => void) | (() => {});
	keyExtractor: (row: any) => React.Key;
	handleUpdate: ((row: any) => void) | (() => {});
	style?: any;
	rowsPerPage?: number;
}

export default function Table({
	columns = [],
	rows = [],
	total = 0,
	rowsPerPage: rowsPerPageParam,
	keyExtractor,
	style = {},
	handleUpdate,
	...props
}: Props) {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageParam ?? constants.DEFAULT_PAGE_SIZE);

	React.useEffect(() => {
		setPage(0);
	}, [total]);

	const handleClick = (event: React.MouseEvent<unknown>, id: number) => props.onRowPress(rows[id]);

	const handleChangePage = async (event: unknown, newPage: number) => {
		const pageFrom = newPage * rowsPerPage;
		const options = { skip: pageFrom, limit: rowsPerPage };
		handleUpdate(options);
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		const rowsPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(rowsPerPage);
		setPage(0);
		const options = { skip: 0, limit: rowsPerPage };
		handleUpdate(options);
	};

	let columnsParsed = columns.map((column, index) => ({
		id: index,
		numeric: false,
		disablePadding: true,
		label: i18next.t(column.toString()),
	}));

	return (
		<Box sx={{ width: "100%", ...style }}>
			<Paper sx={{ width: "100%", mb: 2 }}>
				<EnhancedTableToolbar numSelected={0} filtersComponents={props?.filtersComponents} />
				<TableContainer>
					<MuiTable aria-labelledby="tableTitle" size={"small"}>
						<EnhancedTableHead numSelected={0} rowCount={rows.length} columns={columnsParsed} />
						<TableBody>
							{rows.map((row, index) => {
								return (
									<TableRow
										hover
										onClick={(event) => handleClick(event, index)}
										// role="row"
										tabIndex={-1}
										key={keyExtractor(row)}
										sx={{ cursor: "pointer" }}
									>
										{columns.map((column, index) => (
											<TableCell key={keyExtractor(row).toString() + index} align="left">
												{row[column as keyof typeof row]}
											</TableCell>
										))}
									</TableRow>
								);
							})}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TablePagination
									rowsPerPageOptions={[5, 10, 25]}
									count={total}
									rowsPerPage={rowsPerPage}
									labelRowsPerPage={i18next.t("rows-per-page")}
									page={page}
									labelDisplayedRows={({ from, to, count }) =>
										`${from}-${to} ${i18next.t("of")} ${count}`
									}
									onPageChange={handleChangePage}
									onRowsPerPageChange={handleChangeRowsPerPage}
								/>
							</TableRow>
						</TableFooter>
					</MuiTable>
				</TableContainer>
			</Paper>
		</Box>
	);
}
